import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import PageHeaderText from 'components/base/page-header-text';
import CreateKeywordModal from 'components/keyword-metadata-modal/create-keyword-modal';
import PageText from 'components/base/page-text';
import DeleteModal from 'components/base/delete-modal';
import useDashboardContext from 'contexts/dashboard.context';

import AccessDenied from 'components/access-denied';
import Keyword, { KeywordType } from 'components/metadata-keyword';

const MealMetadata = () => {
  const { category } = useDashboardContext();
  const [loading, setLoading] = useState(false);
  const [keywords, setKeywords] = useState<KeywordType[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [mealId, setMealId] = useState<string>('');
  const [type, setType] = useState<'create' | 'edit'>('create');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { permissions } = useDashboardContext();

  const fetchKeywords = async () => {
    try {
      setLoading(true);
      const res = await apiService.get('keywords', { params: { type: 'meal' } });
      const meal_keyword = res.data.data as KeywordType[];
      const metadata = meal_keyword?.sort((a, b) => {
        const date1 = a.updated_at ?? '';
        const date2 = b.updated_at ?? '';

        return date1.localeCompare(date2);
      });
      setKeywords(metadata);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeywords();
  }, [category]);

  const createMetadata = async (name: string, popular: boolean) => {
    try {
      await apiService.post('keywords', {
        data: {
          name,
          popular,
          type: 'meal'
        }
      });
      setMealId('');
      fetchKeywords();
      toast(<ToastMessage type='success' message='Meal keyword created successfully' />);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem adding keyword' />);
    }
  };
  const editMetaData = async (name: string, id: string, popular: boolean) => {
    try {
      await apiService.put(`keywords/${id}`, {
        data: {
          name,
          popular
        }
      });
      setMealId('');
      fetchKeywords();
      toast(<ToastMessage type='success' message='Meal keyword updated successfully' />);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem updating keyword' />);
    }
  };

  const deleteMetadata = async (id: string) => {
    try {
      await apiService.delete(`keywords/${id}`);
      setMealId('');
      fetchKeywords();
      toast(<ToastMessage type='success' message='Meal keyword deleted successfully' />);
      setShowDeleteModal(false);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem deleting keyword' />);
    }
  };
  const selectedMeal = useMemo(() => {
    return keywords.find((item) => item.id === mealId) ?? null;
  }, [keywords, mealId]);

  const canAccessMetadata = permissions.canAccessMetadata;
  if (!canAccessMetadata) return <AccessDenied />;
  return (
    <div>
      <div className='flex justify-between items-center'>
        <PageHeaderText title='Meal Keywords'>
          <Button
            variant='link'
            color='primary'
            className='bg-primary text-red px-6 py-2 text-sm font-semibold'
            onClick={() => {
              setType('create');
              setShowModal(true);
            }}>
            <AddIcon className='w-5 h-5' />
            Add new
          </Button>
        </PageHeaderText>
      </div>

      {loading ? (
        <PageText className='text-center' text='Loading...' />
      ) : keywords.length === 0 ? (
        <PageText text='No keywords found' />
      ) : (
        <section className='grid md:grid-cols-3 lg:grid-cols-4 gap-6'>
          {keywords.map((keyword, index) => (
            <Keyword
              keyword={keyword}
              key={index}
              onDeleteClick={() => {
                setMealId(keyword.id);
                setShowDeleteModal(true);
              }}
              onEditClick={() => {
                setMealId(keyword.id);

                setType('edit');
                setShowModal(true);
              }}
            />
          ))}
        </section>
      )}

      <CreateKeywordModal
        title='Meal Keyword'
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={(name: string, popular: boolean, mealId: string) => {
          type === 'create' ? createMetadata(name, popular) : editMetaData(name, mealId, popular);
        }}
        selectedMetadata={selectedMeal}
        type={type}
        mealId={mealId}
      />

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={() => deleteMetadata(mealId)}
        type='delete'
        title={`Are you sure you want to delete ${
          keywords.find((item) => item.id === mealId)?.name ?? ''
        }`}
        loading={loading}
        description='This action cannot be undone'
      />
    </div>
  );
};

export default MealMetadata;
